import type { CSSProperties } from "react";
import { Check, Share2 } from "lucide-react";
import { stegaClean } from "@sanity/client/stega";
import type { AiWrappedCard } from "@/sanity/pages/PageAiFeaturesTemplate";
import { ProductImage } from "./ui";
import styles from "./wrapped-stories.module.scss";

export function wrappedStoryDescription(card: AiWrappedCard) {
  const kind = stegaClean(card.storyType);
  const details = [
    card.storySubtitle,
    card.storyMeta,
    card.storyValue,
    card.storyBadge,
  ];
  if (kind === "mood") {
    const sequence = card.storyItems
      ?.slice(0, 3)
      .map((item) => item.label)
      .filter(Boolean);
    if (sequence?.length) details.push(sequence.join(" → "));
  } else {
    card.storyItems?.slice(0, 5).forEach((item) => {
      const value = [item.label, item.value].filter(Boolean).join(": ");
      if (value) details.push(value);
    });
  }
  return details.filter(Boolean).join(". ");
}

export default function WrappedStory({ card }: { card: AiWrappedCard }) {
  const kind = stegaClean(card.storyType);
  if (!kind || kind === "image")
    return (
      <ProductImage
        image={card}
        sizes="(max-width: 639px) 70vw, (max-width: 992px) 300px, 17.66vw"
      />
    );
  const items = card.storyItems?.slice(0, 5) ?? [];
  return (
    <div className={styles.story} data-story-kind={kind} aria-hidden="true">
      <span className={styles.storyDots} />
      <div className={styles.storyContent}>
        {kind === "session" && (
          <>
            <span className={styles.ready} />
            <span className={styles.sessionLabel}>Your wrapped is ready</span>
          </>
        )}
        <div className={styles.storyTitle}>{card.storyTitle}</div>
        {card.storySubtitle && (
          <div className={styles.subtitle}>{card.storySubtitle}</div>
        )}
        {card.storyMeta && <div className={styles.meta}>{card.storyMeta}</div>}
        {card.storyValue && (
          <div className={styles.value} data-story-value={stegaClean(card.storyValue)}>
            {card.storyValue}
          </div>
        )}
        {card.storyBadge && (
          <div className={styles.badge}>{card.storyBadge}</div>
        )}
        {kind === "session" && (
          <span className={styles.sessionAction}>
            <span>Check my wrapped</span> <i />
          </span>
        )}
        {kind === "rituals" && (
          <div className={styles.rules}>
            {items.map((item, i) => (
              <div key={item._key} style={{ "--item": i } as CSSProperties}>
                <span>
                  {item.label}
                  <Check />
                </span>
              </div>
            ))}
          </div>
        )}
        {kind === "best" && (
          <div className={styles.bars}>
            {items.map((item, i) => (
              <div
                className={styles.barRow}
                key={item._key}
                style={
                  {
                    "--item": i,
                    "--bar":
                      Math.min(100, Math.max(0, item.amount ?? 0)) /
                      [96, 78, 16, 62][i % 4],
                  } as CSSProperties
                }
              >
                <span className={styles.barLabel}>{item.label}</span>
                <div className={styles.barLine}>
                  <span className={styles.barFill} />
                  <span className={styles.barValue}>{item.value}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {kind === "mood" && (
          <div className={styles.mood}>
            <span className={styles.moodLine} />
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={styles.moodPoint}
                style={{ "--item": i } as CSSProperties}
              />
            ))}
            {items.slice(0, 3).map((item, i) => (
              <span
                key={item._key}
                className={styles.moodLabel}
                style={{ "--item": i } as CSSProperties}
              >
                {item.label}
              </span>
            ))}
          </div>
        )}
      </div>
      {kind !== "session" && (
        <span className={styles.storyFooter}>
          Share wrapped <Share2 />
        </span>
      )}
    </div>
  );
}
